import { DownloadRounded, UploadFileRounded } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useFeedback } from "../../providers/feedback/feeedback";
import { queryClient } from "../../providers/query/query";
import { downloadFromBlob } from "../../utils/utils";
import { black, greyEnabled, purple, red } from "../../theme/colors";

export interface BulkUploadResult {
  total: number;
  created?: number;
  saved?: number;
  failed: { row: number; errors: string[] }[];
}

interface BulkUploadSectionProps {
  description: string;
  templateFileName: string;
  onDownloadTemplate: () => Promise<any>;
  onUpload: (file: File) => Promise<any>;
  savedLabel: string;
  // Invalidated after a successful upload (anything created/saved), so the
  // underlying list refreshes without the admin needing to reload the page.
  listQueryKey: string;
}

export default function BulkUploadSection({
  description,
  templateFileName,
  onDownloadTemplate,
  onUpload,
  savedLabel,
  listQueryKey,
}: BulkUploadSectionProps) {
  const { showSnackbar } = useFeedback();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<BulkUploadResult | null>(null);

  const handleDownloadTemplate = () => {
    setIsDownloading(true);
    onDownloadTemplate()
      .then((response: any) => downloadFromBlob(response, templateFileName))
      .catch(() => showSnackbar("Something went wrong while downloading the template."))
      .finally(() => setIsDownloading(false));
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setResult(null);
    onUpload(selectedFile)
      .then((response: any) => {
        const data = response?.data?.data as BulkUploadResult;
        setResult(data);
        if ((data?.created ?? data?.saved ?? 0) > 0) {
          queryClient.invalidateQueries({ queryKey: [listQueryKey] });
        }
      })
      .catch((e: any) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong while uploading the file.");
      })
      .finally(() => {
        setIsUploading(false);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      });
  };

  const savedCount = result?.created ?? result?.saved ?? 0;

  return (
    <Box>
      <Typography variant="body2" color={greyEnabled} mb={2}>
        {description}
      </Typography>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <Button
          variant="outlined"
          startIcon={<DownloadRounded />}
          onClick={handleDownloadTemplate}
          disabled={isDownloading}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {isDownloading ? "Downloading..." : "Download Template"}
        </Button>

        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileRounded />}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {selectedFile ? selectedFile.name : "Choose File"}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".xlsx"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          />
        </Button>

        <Button
          variant="contained"
          disableElevation
          disabled={!selectedFile || isUploading}
          onClick={handleUpload}
          sx={{ bgcolor: purple, textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "#7A2074" } }}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </Box>

      {result && (
        <Box mt={2}>
          <Alert severity={result.failed.length ? "warning" : "success"} sx={{ mb: 1.5 }}>
            {result.total} row{result.total === 1 ? "" : "s"} processed — {savedCount} {savedLabel}
            {result.failed.length > 0 && `, ${result.failed.length} failed`}.
          </Alert>
          {result.failed.length > 0 && (
            <Box sx={{ maxHeight: 220, overflow: "auto", border: "1px solid #ECECEC", borderRadius: 2, p: 1.5 }}>
              {result.failed.map((failure) => (
                <Box key={failure.row} mb={1}>
                  <Typography variant="body2" fontWeight={700} color={black}>
                    Row {failure.row}
                  </Typography>
                  {failure.errors.map((error, index) => (
                    <Typography key={index} variant="body2" color={red} sx={{ ml: 1 }}>
                      • {error}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
